import React, { useState, useEffect, useContext, useRef } from 'react';
import { Form, Input, Button, Select, InputNumber, message, Checkbox, Upload } from 'antd';
import { Context } from '../../../defaults';
import {
  getTopicsBySubjectId,
  getQuestionTypes,
  getQuestionLevels,
  getLanguagesType,
  getTeachersSubjects,
  createQuestions,
} from '../../../actions';
import { InboxOutlined } from '@ant-design/icons';
import { positiveIntegerRegex, integerRegex } from '../../../common';
import JoditEditor from 'jodit-react';
import TestQuestion from './TestQuestion';
import OpenQuestion from './OpenQuestion';
import DropAndDragQuestion from './DropAndDragQuestion';
import MissingWordsQuestion from './MissingWordsQuestion';

const QuestionsAdd = ({ setAdded }) => {
  const { dictionary, language, setLoading } = useContext(Context);
  const [topics, setTopics] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [languagesType, setLanguagesType] = useState([]);
  const [questionLevels, setQuestionLevels] = useState([]);
  const [form] = Form.useForm();
  const [questionType, setQuestionType] = useState(null);
  const [teacherSubjects, setTeacherSubjects] = useState();
  const editor = useRef(null);
  const [questionStates, setQuestionStates] = useState({
    withFile: false,
    point: null,
    questionFileCount: null,
  });
  const [fileLists, setFileLists] = useState([]);
  const [files, setFiles] = useState([]);
  const [questionAnswerFiles, setQuestionAnswerFiles] = useState([]);
  const [multiQuestionAnswerFiles, setMultiQuestionAnswerFiles] = useState([
    { index: 0, fileLists: [] },
  ]);
  const config = {
    readonly: false,
    limitChars: 2000,
    placeholder: '',
  };

  useEffect(() => {
    getQuestionTypes().then(({ data }) => setQuestionTypes(data));
    getLanguagesType().then(({ data }) => setLanguagesType(data));
    getQuestionLevels().then(({ data }) => setQuestionLevels(data));
    getTeachersSubjects().then(({ data }) => setTeacherSubjects(data));
  }, []);

  const fetchTopics = (id) => {
    getTopicsBySubjectId(id).then(({ data }) => {
      setTopics(data);
    });
  };

  const createQuestionsByType = () => {
    if (questionType === 1) {
      if (form.getFieldValue('tests').some((el) => !!el.isRight)) {
        const formData = new FormData();
        files.length > 0 &&
          files.forEach(
            (file, index) =>
              index < 2 &&
              formData.append('questionFiles', file.originFileObj, file.originFileObj.name),
          );

        multiQuestionAnswerFiles.length > 0 &&
          multiQuestionAnswerFiles.map((el) => {
            return el.fileLists.forEach(
              (file, index) =>
                index < 2 &&
                formData.append(
                  'questionAnswerFiles',
                  file.originFileObj,
                  `${index} ${el.index} ${file.originFileObj.name}`,
                ),
            );
          });

        formData.append(
          'question',
          JSON.stringify({
            ...form.getFieldsValue(),
            answerVariants: form.getFieldValue('tests').map((el) => {
              return { ...el, isRight: el.isRight === true ? 1 : 0 };
            }),
          }),
        );
        return createQuestions(formData);
      } else {
        message.error(dictionary?.OnlyOneAnswerCountValidation[language], 4);
      }
    } else if (questionType === 2) {
      const formData = new FormData();
      files.length > 0 &&
        files.forEach(
          (file, index) =>
            index < 2 &&
            formData.append('questionFiles', file.originFileObj, file.originFileObj.name),
        );

      questionAnswerFiles.length > 0 &&
        questionAnswerFiles.forEach(
          (file, index) =>
            index < 2 &&
            formData.append('questionAnswerFiles', file.originFileObj, file.originFileObj.name),
        );

      formData.append(
        'question',
        JSON.stringify({
          ...form.getFieldsValue(),
          answerVariants: [
            {
              isRight: 1,
              answerHtml: form.getFieldValue('answerHtml'),
            },
          ],
        }),
      );
      return createQuestions(formData);
    } else if (questionType === 3) {
      const formData = new FormData();
      files.length > 0 &&
        files.forEach(
          (file, index) =>
            index < 2 &&
            formData.append('questionFiles', file.originFileObj, file.originFileObj.name),
        );

      multiQuestionAnswerFiles.length > 0 &&
        multiQuestionAnswerFiles.map((el) => {
          return el.fileLists.forEach(
            (file, index) =>
              index < 2 &&
              formData.append(
                'questionAnswerFiles',
                file.originFileObj,
                `${index} ${el.index} ${file.originFileObj.name}`,
              ),
          );
        });

      formData.append(
        'question',
        JSON.stringify({
          ...form.getFieldsValue(),
          answerVariants: form.getFieldValue('sorts').map((el, index) => {
            return { ...el, sortOrder: index };
          }),
        }),
      );
      return createQuestions(formData);
    } else if (questionType === 4) {
      const formData = new FormData();

      files.length > 0 &&
        files.forEach(
          (file, index) =>
            index < 2 &&
            formData.append('questionFiles', file.originFileObj, file.originFileObj.name),
        );

      formData.append(
        'question',
        JSON.stringify({
          ...form.getFieldsValue(),
          answerVariants: form.getFieldValue('missingWords').map((el, index) => {
            return { answerText: el.answerText, sortOrder: index };
          }),
        }),
      );
      return createQuestions(formData);
    } else if (questionType === 5) {
      if (form.getFieldValue('missingWords').some((el) => !!el.isRight)) {
        const formData = new FormData();
        files.length > 0 &&
          files.forEach(
            (file, index) =>
              index < 2 &&
              formData.append('questionFiles', file.originFileObj, file.originFileObj.name),
          );

        formData.append(
          'question',
          JSON.stringify({
            ...form.getFieldsValue(),
            answerVariants: form.getFieldValue('missingWords').map((el, index) => {
              return { ...el, sortOrder: index, isRight: el.isRight === true ? 1 : 0 };
            }),
          }),
        );
        return createQuestions(formData);
      } else {
        message.error(dictionary?.MinAnswerCountValidation[language], 4);
      }
    }
  };

  function prepareFiles(file) {
    file.onSuccess('ok');
  }

  const onCreateQuestions = () => {
    setLoading(true);
    form
      .validateFields()
      .then(() => {
        createQuestionsByType()
          .then(() => {
            setLoading(false);
            setQuestionType(null);
            message.success(dictionary?.SuccessfullyAdded[language], 4);
            form.resetFields();
            setQuestionStates({ withFile: false, point: null });
            setFiles([]);
            setQuestionAnswerFiles([]);
            setFileLists([]);
            setMultiQuestionAnswerFiles([{ index: 0, fileLists: [] }]);
          })
          .catch(() => {
            message.error(dictionary?.SomethingWentWrong[language], 4);
            setLoading(false);
          });
      })
      .catch((e) => {
        debugger;
        if (e.name !== 'TypeError') {
          message.error(dictionary?.FormValidationFail[language], 4);
        }
        setLoading(false);
      });
  };

  return (
    <React.Fragment>
      {/* <h1 style={{ textAlign: 'center' }}>{dictionary?.QuestionsControlPanel[language]}</h1> */}
      <Form
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 12 }}
        form={form}
        name="nest-messages"
        initialValues={{
          tests: [{ isRight: false, answerHtml: null }],
          sorts: [{ answerHtml: null }],
          missingWords:
            questionType === 4 ? [{ answerText: null }] : [{ answerText: null, isRight: false }],
        }}>
        <Form.Item
          name="subjectId"
          label={dictionary?.Subject[language]}
          rules={[
            {
              required: true,
            },
          ]}>
          <Select
            onChange={(e, data) => {
              fetchTopics(data.value);
            }}
            placeholder={dictionary?.Subject[language]}>
            {teacherSubjects &&
              teacherSubjects.map((el) => {
                return (
                  <Select.Option value={el.id} key={el.id}>
                    {el.name}
                  </Select.Option>
                );
              })}
          </Select>
        </Form.Item>
        <Form.Item
          name="topicId"
          label={dictionary?.Topic[language]}
          rules={[
            {
              required: true,
            },
          ]}>
          <Select
            onChange={(e) => {
              form.setFieldsValue({
                langId: teacherSubjects.find(
                  (i) => i.id === topics.find((i) => i.id === e).subjectId,
                ).langId,
                yearOfStudy: teacherSubjects.find(
                  (i) => i.id === topics.find((i) => i.id === e).subjectId,
                ).studyYear,
              });
            }}
            placeholder={dictionary?.TopicName[language]}>
            {topics &&
              topics.map((el) => {
                return (
                  <Select.Option value={el.id} key={el.id}>
                    {el.name}
                  </Select.Option>
                );
              })}
          </Select>
        </Form.Item>
        <Form.Item
          name="yearOfStudy"
          label={dictionary?.YearOfStudy[language]}
          rules={[
            { required: true },
            { pattern: integerRegex, message: dictionary?.OnlyNumber[language] },
          ]}>
          <InputNumber
            disabled={true}
            placeholder={dictionary?.StudyYear[language]}
            style={{ width: 150 }}
          />
        </Form.Item>
        <Form.Item
          name="langId"
          label={dictionary?.Lang[language]}
          rules={[
            {
              required: true,
            },
          ]}>
          <Select disabled={true} placeholder={dictionary?.Lang[language]}>
            {languagesType &&
              languagesType.map((el) => (
                <Select.Option value={el.id} key={el.id}>
                  {el.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="questionTypeId"
          label={dictionary?.QuestionType[language]}
          rules={[
            {
              required: true,
            },
          ]}>
          <Select
            onChange={(e, data) => {
              setQuestionType(data.value);
            }}
            placeholder={dictionary?.QuestionType[language]}>
            {questionTypes &&
              questionTypes.map((el) => (
                <Select.Option value={el.id} key={el.id}>
                  {el.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="questionHtml"
          label={dictionary?.Question[language]}
          rules={[
            {
              required: true,
            },
            {
              max: 2000,
              type: 'string',
              message: dictionary?.MaxCharacterCount[language].replace('%s', 2000),
            },
          ]}>
          <JoditEditor ref={editor} config={config} tabIndex={1} />
        </Form.Item>
        <Form.Item
          name="questionFiles"
          label={
            <div>
              {dictionary?.AttachFile[language]} :
              <Checkbox
                onChange={(e) =>
                  setQuestionStates({ ...questionStates, withFile: e.target.checked })
                }
                checked={questionStates.withFile}
                size="large"
              />
            </div>
          }
          colon={false}>
          <Upload.Dragger
            customRequest={(e) => prepareFiles(e)}
            maxCount={2}
            disabled={!questionStates.withFile}
            onChange={({ file, fileList }) => {
              if (file.status === 'removed') {
                setFileLists(fileLists.filter((f) => f.index !== file.index));
                setFiles(files.filter((f) => f.uid !== file.file.uid));
              } else if (file.status === 'done') {
                if (file.size <= 5000000) {
                  if (fileLists.length < 2) {
                    setFiles([...files, file]);
                    setFileLists([
                      ...fileLists,
                      {
                        file: file,
                        name: file.name,
                        index: fileLists.length,
                        uid: Date.now().toString(),
                      },
                    ]);
                  } else {
                    message.error('Максимум 2 файла');
                  }
                } else {
                  message.error('Не больше 5 МВ');
                }
              }
            }}
            fileList={fileLists}
            multiple={true}
            style={{ height: '15px', width: '100%' }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{dictionary?.MaxFile[language]}</p>
          </Upload.Dragger>
        </Form.Item>
        {questionType === 1 && (
          <TestQuestion
            dictionary={dictionary}
            language={language}
            form={form}
            multiQuestionAnswerFiles={multiQuestionAnswerFiles}
            setMultiQuestionAnswerFiles={setMultiQuestionAnswerFiles}
          />
        )}
        {questionType === 2 && (
          <OpenQuestion
            dictionary={dictionary}
            language={language}
            form={form}
            questionAnswerFiles={questionAnswerFiles}
            setQuestionAnswerFiles={setQuestionAnswerFiles}
          />
        )}
        {questionType === 3 && (
          <DropAndDragQuestion
            dictionary={dictionary}
            language={language}
            form={form}
            multiQuestionAnswerFiles={multiQuestionAnswerFiles}
            setMultiQuestionAnswerFiles={setMultiQuestionAnswerFiles}
          />
        )}
        {questionType === 4 && <MissingWordsQuestion dictionary={dictionary} language={language} />}
        {questionType === 5 && (
          <MissingWordsQuestion
            dictionary={dictionary}
            language={language}
            withChecking={true}
            form={form}
          />
        )}
        <Form.Item
          name="questionLevel"
          label={dictionary?.Level[language]}
          rules={[
            {
              required: true,
            },
          ]}>
          <Select placeholder={dictionary?.Level[language]}>
            {questionLevels &&
              questionLevels.map((el) => {
                return (
                  <Select.Option value={el.level} key={el.level}>
                    {el.description}
                  </Select.Option>
                );
              })}
          </Select>
        </Form.Item>
        <Form.Item
          name="point"
          label={dictionary?.Point[language]}
          rules={[
            { required: true },
            { pattern: positiveIntegerRegex, message: dictionary?.OnlyNumber[language] },
          ]}>
          <InputNumber
            value={questionStates.point}
            onChange={(e) => {
              setQuestionStates({ ...questionStates, point: e });
            }}
            style={{ width: 250 }}
            placeholder={dictionary?.Point[language]}
          />
        </Form.Item>
        <Form.Item name="comment" label={dictionary?.Comment[language]}>
          <Input placeholder={dictionary?.Comment[language]} />
        </Form.Item>
        <Form.Item name="hintText" label={dictionary?.Hint[language]}>
          <Input placeholder={dictionary?.Hint[language]} />
        </Form.Item>
        <Form.Item
          name="hintPoint"
          label={dictionary?.HintPoint[language]}
          rules={[
            { pattern: positiveIntegerRegex, message: dictionary?.OnlyNumber[language] },
            {
              type: 'number',
              max: questionStates.point,
              message: dictionary?.HintPointValidation[language],
            },
          ]}>
          <InputNumber placeholder={dictionary?.HintPoint[language]} style={{ width: 250 }} />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 6 }}>
          <Button type="primary" htmlType="submit" onClick={() => onCreateQuestions()}>
            {dictionary?.Save[language]}
          </Button>
        </Form.Item>
      </Form>
    </React.Fragment>
  );
};

export default QuestionsAdd;
